import { use, useMemo } from 'react'

import { useTranslation } from 'react-i18next'

import { ChatContext } from '@app/context'
import {
  getBaseInfo,
  getContextEntity,
  getEmailWithFallback,
  isB2C,
  shouldFallbackToAccount,
} from '@app/helpers'
import { useConfiguration, useIsStaff } from '@app/hooks'
import { useParticipantLabels } from '@app/hooks/useParticipantLabels'
import { useUser } from '@app/hooks/user/useUser'
import { Conversation } from '@app/models'

import { getAppUrl, isB2BView, isBuyerView } from '@app/utils'

import { getParticipantJobTitle } from './helpers'
import { SidebarParticipant } from './types'

export const useParticipants = (
  conversation: Conversation
): SidebarParticipant[] => {
  const { state } = use(ChatContext)
  const { t } = useTranslation()
  const configuration = useConfiguration()
  const { currentUser } = useUser()
  const participantLabelsState = useParticipantLabels()
  const isStaff = useIsStaff()
  const isSubtitleReady =
    !isStaff ||
    isBuyerView() ||
    !!participantLabelsState.loadedByConversationId[conversation.id] ||
    !!participantLabelsState.failedByConversationId[conversation.id]

  const listing = getContextEntity(state.listings, conversation)

  return useMemo(() => {
    const participantsWithDetails: SidebarParticipant[] = (
      conversation.participants || []
    ).map((participant) => {
      const isCurrentUser = participant?.userId === currentUser.id

      const showAccount = shouldFallbackToAccount(participant, conversation)

      const transactionType = listing?.transactionType || null

      const isB2CParticipant = isB2C(participant)

      const { avatar, language, country, fullName, phone } = getBaseInfo(
        state,
        participant,
        conversation
      )

      const labels =
        participantLabelsState.byParticipantId[participant.id] || []

      const jobTitle = showAccount
        ? ''
        : getParticipantJobTitle({
          participant,
          transactionType,
          currentUserId: currentUser.id,
          t,
        })

      const email =
        showAccount ||
        (!isB2BView() && !isB2CParticipant && participant?.userId)
          ? ''
          : getEmailWithFallback(participant)

      const url =
        !isB2BView() &&
        !showAccount &&
        participant?.isAgent &&
        participant?.slug
          ? getAppUrl(configuration, `/a/${participant.slug}`)
          : ''

      return {
        avatar,
        jobTitle,
        isSubtitleReady,
        isB2CParticipant,
        isPrivateAdvertiser: participant.isPrivateAdvertiser,
        phone,
        email,
        language,
        country,
        fullName,
        url,
        isCurrentUser,
        id: participant.id,
        obfuscate: !!participant.hasDetailsHidden,
        labels,
        shouldFallbackToAccount: showAccount,
      }
    })

    return participantsWithDetails.sort((a, b) =>
      a.fullName.localeCompare(b.fullName)
    )
  }, [
    conversation.participants,
    configuration,
    listing,
    state.accounts,
    currentUser,
    isSubtitleReady,
    participantLabelsState.byParticipantId,
  ])
}
